import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { useThemeContext } from "../context/themeContext";

/**
 * Opinionated Dialog wrapper that handles mobile/touch positioning consistently.
 *
 * Two independent signals control layout:
 *   isFullscreen — viewport width signal. Defaults to breakpoints.down("sm") internally,
 *                  but callers can pass it explicitly if they've already derived it.
 *   isTouchDevice — hardware check from ThemeContext, always derived internally.
 *                   Controls vertical snap-to-top so the software keyboard
 *                   doesn't push the dialog off screen.
 *
 * Optional props:
 *   topOffset — CSS value (e.g. "8vh"). When provided, the dialog sits this far from the
 *               top on non-touch devices. On touch devices it is ignored and the dialog
 *               snaps fully to the top (no padding) so the keyboard doesn't obscure it.
 *
 * Note: sx passed by the caller is merged with the internal container sx via MUI's array
 * syntax, so both apply safely. PaperProps will override the standardised mobile styles —
 * use DialogContent sx for inner styling instead.
 */
function StandardDialog({ isFullscreen: isFullscreenProp, topOffset, sx: callerSx, children, ...props }) {
  const { isTouchDevice } = useThemeContext();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Use caller-supplied value if provided, otherwise fall back to breakpoint check
  const isFullscreen = isFullscreenProp ?? isSmallScreen;

  // Build container alignment:
  // - Touch: always snap fully to top so the software keyboard doesn't obscure content.
  //   Also padded by the safe-area insets (0px outside the installed-PWA/notched case) so
  //   a fullscreen dialog's own top/bottom edges don't sit under the status bar or home
  //   indicator — StandardDialog's callers (LibraryModal, CollectionDetailRoute, search)
  //   all get this for free rather than handling it per dialog.
  // - Non-touch with topOffset: sit near the top with padding (e.g. search dialogs where
  //   results appear below)
  // - Non-touch without topOffset: MUI default (vertically centred)
  const containerSx = isTouchDevice
    ? {
        alignItems: "flex-start",
        paddingTop: "var(--safe-top)",
        paddingBottom: "var(--safe-bottom)",
      }
    : topOffset
      ? { alignItems: "flex-start", paddingTop: topOffset }
      : null;

  const internalSx = containerSx ? { "& .MuiDialog-container": containerSx } : null;

  // Merge internal and caller sx using MUI's array syntax so both apply independently.
  // internalSx targets .MuiDialog-container; callerSx typically targets .MuiDialog-paper —
  // different elements, so they compose without conflict.
  const mergedSx = [internalSx, callerSx].filter(Boolean);

  return (
    <Dialog
      // On narrow viewports, bypass MUI's maxWidth and fill the screen with even margins
      fullWidth={!isFullscreen}
      PaperProps={
        isFullscreen
          ? { sx: { width: "100%", maxWidth: "100%", m: 2, borderRadius: 1 } }
          : undefined
      }
      sx={mergedSx.length ? mergedSx : undefined}
      {...props}
    >
      {children}
    </Dialog>
  );
}

export default StandardDialog;
